import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, FileSpreadsheet, FileText, File, Folder, ChevronRight, LoaderCircle, AlertCircle, RefreshCw, Home, TriangleAlert } from 'lucide-react';
import GoogleDriveIcon from '../../icons/googleDrive';
import { listGoogleDriveFiles, GoogleDriveFile } from '../../service/apiService';

// MIME types where Google creates a converted copy instead of editing the original.
// PDF and plain text files get duplicated when opened via Google Docs,
// so revision-based change detection won't work reliably for these.
const NON_NATIVE_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain',
]);

interface GoogleDriveFilePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onFileSelect: (file: GoogleDriveFile) => void;
}

interface BreadcrumbItem {
  id: string | null; // null = root
  name: string;
}

const MIME_TYPE_LABELS: Record<string, string> = {
  'application/vnd.google-apps.spreadsheet': 'Google Sheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-excel': 'Excel',
  'text/csv': 'CSV',
  'application/vnd.google-apps.document': 'Google Doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/pdf': 'PDF',
  'application/vnd.google-apps.presentation': 'Google Slides',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  'application/vnd.google-apps.drawing': 'Google Drawing',
  'text/plain': 'Text',
  'application/vnd.google-apps.folder': 'Folder',
};

const getFileIcon = (mimeType: string) => {
  if (mimeType === 'application/vnd.google-apps.folder') {
    return <Folder size={20} className="text-yellow-500" />;
  }
  if (
    mimeType.includes('spreadsheet') ||
    mimeType.includes('excel') ||
    mimeType === 'text/csv'
  ) {
    return <FileSpreadsheet size={20} className="text-green-600" />;
  }
  if (mimeType.includes('document') || mimeType.includes('word')) {
    return <FileText size={20} className="text-blue-600" />;
  }
  if (mimeType.includes('presentation') || mimeType.includes('drawing')) {
    return <FileText size={20} className="text-orange-500" />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText size={20} className="text-red-600" />;
  }
  if (mimeType === 'text/plain') {
    return <FileText size={20} className="text-gray-600" />;
  }
  return <File size={20} className="text-gray-500" />;
};

const formatFileSize = (sizeStr?: string): string => {
  if (!sizeStr) return '';
  const bytes = parseInt(sizeStr, 10);
  if (isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const GoogleDriveFilePicker: React.FC<GoogleDriveFilePickerProps> = ({
  isOpen,
  onClose,
  onFileSelect,
}) => {
  const [files, setFiles] = useState<GoogleDriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedFile, setSelectedFile] = useState<GoogleDriveFile | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: 'My Drive' }]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const currentFolderId = breadcrumbs[breadcrumbs.length - 1]?.id || undefined;

  const fetchFiles = useCallback(async (query?: string, pageToken?: string, append = false, folderId?: string) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const result = await listGoogleDriveFiles(pageToken, query || undefined, 50, folderId);

      if (!result.success) {
        setError('Failed to load files. Make sure Google Drive is connected.');
        return;
      }

      if (append) {
        setFiles(prev => [...prev, ...result.files]);
      } else {
        setFiles(result.files);
      }
      setNextPageToken(result.next_page_token);
    } catch (err) {
      setError('Failed to load Google Drive files.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setSearchQuery('');
      setSelectedFile(null);
      setNextPageToken(undefined);
      setError(null);
      setBreadcrumbs([{ id: null, name: 'My Drive' }]);
      fetchFiles();
    }
  }, [isOpen, fetchFiles]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setNextPageToken(undefined);
      // Search across all files (no folder filter)
      if (value) {
        fetchFiles(value, undefined, false, undefined);
      } else {
        fetchFiles(undefined, undefined, false, currentFolderId);
      }
    }, 400);
  };

  const handleLoadMore = () => {
    if (nextPageToken && !loadingMore) {
      fetchFiles(searchQuery, nextPageToken, true, searchQuery ? undefined : currentFolderId);
    }
  };

  const handleScroll = () => {
    if (!listRef.current || !nextPageToken || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      handleLoadMore();
    }
  };

  const handleFolderClick = (folder: GoogleDriveFile) => {
    setSearchQuery('');
    setSelectedFile(null);
    setNextPageToken(undefined);
    setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
    fetchFiles(undefined, undefined, false, folder.id);
  };

  const handleBreadcrumbClick = (index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setSearchQuery('');
    setSelectedFile(null);
    setNextPageToken(undefined);
    const folderId = newBreadcrumbs[newBreadcrumbs.length - 1]?.id || undefined;
    fetchFiles(undefined, undefined, false, folderId);
  };

  const handleSelect = () => {
    if (selectedFile && selectedFile.mimeType !== 'application/vnd.google-apps.folder') {
      onFileSelect(selectedFile);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 scrim flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 flex flex-col" style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900 flex items-center">
            <GoogleDriveIcon size={18} className="mr-2" />
            Select a file from Google Drive
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white text-gray-900 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Breadcrumbs */}
        {!searchQuery && breadcrumbs.length > 0 && (
          <div className="px-6 py-2 border-b border-gray-100 flex items-center gap-1 text-sm overflow-x-auto">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />}
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0 ${
                    index === breadcrumbs.length - 1
                      ? 'text-violet-600 font-medium'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {index === 0 && <Home size={14} />}
                  <span className="truncate max-w-[150px]">{crumb.name}</span>
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* File List */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-2 py-2"
          style={{ minHeight: '300px' }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <LoaderCircle className="w-8 h-8 text-violet-600 animate-spin mb-3" />
              <p className="text-sm text-gray-500">Loading files...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-8 h-8 text-red-400 mb-3" />
              <p className="text-sm text-red-600 mb-3">{error}</p>
              <button
                onClick={() => fetchFiles(searchQuery, undefined, false, currentFolderId)}
                className="text-sm text-violet-600 hover:text-violet-700 flex items-center gap-1"
              >
                <RefreshCw size={14} />
                Try again
              </button>
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <File className="w-8 h-8 text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">
                {searchQuery ? 'No files match your search' : 'This folder is empty'}
              </p>
            </div>
          ) : (
            <>
              {files.map((file) => {
                const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
                const isSelected = selectedFile?.id === file.id;

                return (
                  <button
                    key={file.id}
                    onClick={() => {
                      if (isFolder) {
                        handleFolderClick(file);
                      } else {
                        setSelectedFile(isSelected ? null : file);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-left transition-colors ${
                      isSelected
                        ? 'bg-violet-50 border border-violet-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    } cursor-pointer`}
                  >
                    <div className="flex-shrink-0">
                      {file.iconLink ? (
                        <img src={file.iconLink} alt="" className="w-5 h-5" />
                      ) : (
                        getFileIcon(file.mimeType)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{MIME_TYPE_LABELS[file.mimeType] || 'File'}</span>
                        {file.size && (
                          <>
                            <span className="text-gray-300">|</span>
                            <span>{formatFileSize(file.size)}</span>
                          </>
                        )}
                        {file.modifiedTime && (
                          <>
                            <span className="text-gray-300">|</span>
                            <span>{formatDate(file.modifiedTime)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {isFolder && (
                      <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                    )}
                    {isSelected && !isFolder && (
                      <ChevronRight size={16} className="text-violet-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })}

              {loadingMore && (
                <div className="flex justify-center py-3">
                  <LoaderCircle className="w-5 h-5 text-violet-600 animate-spin" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Non-native file warning */}
        {selectedFile && NON_NATIVE_MIME_TYPES.has(selectedFile.mimeType) && (
          <div className="mx-6 mt-3 flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <TriangleAlert className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 leading-relaxed">
              <span className="font-semibold">Limited monitoring support:</span>{' '}
              {MIME_TYPE_LABELS[selectedFile.mimeType] || 'This file type'} files are not natively editable in Google Drive.
              When edited via Google Docs, a converted copy is created instead of modifying the original file,
              so changes may not be detected. For reliable monitoring, use <span className="font-semibold">Google Sheets</span>,{' '}
              <span className="font-semibold">Google Docs</span>, or <span className="font-semibold">Google Slides</span> files.
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {selectedFile
              ? `Selected: ${selectedFile.name}`
              : 'Select a file to monitor for changes'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedFile}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Select File
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleDriveFilePicker;
